import { useEffect, useRef, useState } from 'react';
import type { EntitlementMode, SolTemporalKpiSnapshot } from '@economi/domain-contracts';
import { formatSolesFromCents } from '../../services/solImpactEngine';
import styles from './HeaderBar.module.css';

export type ActiveViewTab =
  | 'DASHBOARD_TELEMETRY'
  | 'EXPENSES_ANALYTICS'
  | 'CYCLE_AND_INFLOWS'
  | 'RECURRING_EXPENSES'
  | 'RECEIPT_OCR_SCANNER'
  | 'ENTITLEMENT_AND_API';

interface HeaderBarProps {
  readonly snapshot: SolTemporalKpiSnapshot;
  readonly userEmail?: string;
  readonly userFullName?: string;
  readonly userPhone?: string | null;
  readonly userOccupation?: string | null;
  readonly onLogout?: () => void;
  readonly activeTab?: ActiveViewTab;
  readonly onSelectTab?: (tab: ActiveViewTab) => void;
  readonly entitlementMode?: EntitlementMode;
  readonly apiOnline?: boolean;
  readonly apiLatencyMs?: number;
}

export function HeaderBar({
  snapshot,
  userEmail,
  userFullName,
  userPhone,
  userOccupation,
  onLogout,
}: HeaderBarProps) {
  const [isUserPopoverOpen, setIsUserPopoverOpen] = useState<boolean>(false);
  const popoverContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isUserPopoverOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverContainerRef.current &&
        !popoverContainerRef.current.contains(event.target as Node)
      ) {
        setIsUserPopoverOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsUserPopoverOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isUserPopoverOpen]);

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        <div className={styles.topRow}>
          <div className={styles.brandGroup}>
            <div className={styles.logoMark}>S/</div>
            <div>
              <div className={styles.brandTitle}>
                EconomiController
                <span className={styles.versionTag}>Control de Tarjetas</span>
              </div>
            </div>
          </div>

          {userEmail && (
            <div className={styles.userMenuWrapper} ref={popoverContainerRef}>
              <button
                type="button"
                className={`${styles.userTriggerButton} ${
                  isUserPopoverOpen ? styles.userTriggerButtonOpen : ''
                }`}
                onClick={() => setIsUserPopoverOpen((prev) => !prev)}
                aria-expanded={isUserPopoverOpen}
                aria-haspopup="dialog"
                title="Ver información del usuario, renovación de ingresos y cerrar sesión"
              >
                <span className={styles.userAvatarIcon} aria-hidden="true">
                  👤
                </span>
                <span className={styles.userTriggerEmail}>{userEmail}</span>
                <span className={styles.userTriggerChevron} aria-hidden="true">
                  {isUserPopoverOpen ? '▴' : '▾'}
                </span>
              </button>

              {isUserPopoverOpen && (
                <div
                  className={styles.userPopover}
                  role="dialog"
                  aria-label="Información del usuario y ciclo de renovación"
                >
                  <div className={styles.popoverHeader}>
                    <div className={styles.popoverAvatarLarge}>
                      {(userFullName || userEmail).charAt(0).toUpperCase()}
                    </div>
                    <div className={styles.popoverIdentity}>
                      <p className={styles.popoverUserName}>
                        {userFullName || 'Usuario EconomiController'}
                      </p>
                      <p className={styles.popoverUserEmail}>{userEmail}</p>
                      {(userOccupation || userPhone) && (
                        <p className={styles.popoverUserMeta}>
                          {[userOccupation, userPhone].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.popoverCloseBtn}
                      onClick={() => setIsUserPopoverOpen(false)}
                      aria-label="Cerrar ventana de usuario"
                    >
                      ×
                    </button>
                  </div>

                  <div className={styles.renewalSection}>
                    <div className={styles.renewalSectionTitle}>
                      📅 Información de Renovación del Ciclo
                    </div>

                    <div className={styles.renewalInfoRow}>
                      <span className={styles.renewalLabel}>Fecha de Renovación:</span>
                      <span className={styles.renewalValueHighlight}>
                        {snapshot.renewalDateIso} ({snapshot.remainingDays} días restantes)
                      </span>
                    </div>

                    <div className={styles.renewalInfoRow}>
                      <span className={styles.renewalLabel}>Cuota Diaria Segura:</span>
                      <span className={styles.renewalValueMono}>
                        {formatSolesFromCents(snapshot.dynamicSafeDailyAllowanceCents)}/día
                      </span>
                    </div>

                    <div className={styles.renewalInfoRow}>
                      <span className={styles.renewalLabel}>
                        Límite de Gasto ({snapshot.spendingRatePercent}%):
                      </span>
                      <span className={styles.renewalValueMono}>
                        {formatSolesFromCents(snapshot.effectiveSpendingCapCents)}
                      </span>
                    </div>

                    <div className={styles.renewalInfoRow}>
                      <span className={styles.renewalLabel}>Capacidad Restante:</span>
                      <span className={styles.renewalValueEmerald}>
                        {formatSolesFromCents(snapshot.remainingCapCents)} (
                        {snapshot.remainingSpendingCapacityPercent.toFixed(1)}%)
                      </span>
                    </div>
                  </div>

                  {onLogout && (
                    <div className={styles.popoverFooter}>
                      <button
                        type="button"
                        className={styles.logoutPopoverButton}
                        onClick={() => {
                          setIsUserPopoverOpen(false);
                          onLogout();
                        }}
                      >
                        🚪 Cerrar Sesión
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
